// Appels à l'API PUBLIQUE des formulaires joints aux dossiers (« VisaTravelApp backend/formulairesPublics/routes.js »,
// préfixe ${API_BASE_URL}/formulaires-publics, plan §5.2). Aucune auth : le jeton (48 hex) est le secret.
// Toutes les fonctions rejettent avec une ErreurPublique normalisée (normaliserErreur), jamais l'erreur axios brute :
// l'écran ne connaît que { status, code?, message, etat?, erreurs?, ecriture? }.
// Test : formulairesPublicsServices.test.ts (axios mocké ; aucun appel réseau).
import axios from 'axios';
import type { AxiosProgressEvent, AxiosRequestConfig } from 'axios';
import { API_BASE_URL } from '../config/api';
import type {
  ErreurPublique, ErreurSoumission, EtatEcriture, FormulaireCharge, Reponses, ReponseSoumission, ReponseUpload,
  StatutInstance, ValeurReponse,
} from './types';

const PREFIXE = '/formulaires-publics';
// Délai des appels JSON ; aucun délai sur l'upload (20 Mo possibles sur un réseau mobile)
const DELAI_MS = 30000;
const DELAI_UPLOAD_MS = 10 * 60 * 1000; // upload : délai de sécurité large (20 Mo en mobile), jamais infini

// Messages par défaut quand le corps de la réponse n'en donne pas (page HTML de nginx, 500 sans JSON, 422…)
const MESSAGE_RESEAU = 'Connexion impossible : vérifiez votre accès internet puis réessayez';
const MESSAGE_DELAI = 'Le serveur ne répond pas : réessayez dans quelques instants';
const MESSAGE_INDISPONIBLE = 'Formulaire temporairement indisponible, réessayez dans quelques minutes';
const MESSAGES_PAR_STATUT: Record<number, string> = {
  400: 'Requête refusée par le serveur',
  404: 'Lien inconnu',
  409: 'Le formulaire a été modifié entre-temps : rechargez-le',
  410: "Ce lien n'est plus actif. Contactez contact@visamonde.com.",
  413: 'Fichier trop volumineux (20 Mo max)',
  422: 'Certaines réponses sont invalides ou manquantes : corrigez les champs signalés',
  429: 'Trop de requêtes, réessayez dans une minute',
  500: 'Erreur du serveur, réessayez dans quelques minutes',
  502: MESSAGE_INDISPONIBLE,
  503: MESSAGE_INDISPONIBLE,
  504: MESSAGE_INDISPONIBLE,
};

// ---------------------------------------------------------------------------
// URLs
// ---------------------------------------------------------------------------

const urlInstance = (jeton: string): string => `${API_BASE_URL}${PREFIXE}/${jeton}`;

// GET /:jeton/fichiers/:nom (relecture d'un fichier déposé ; lien target=_blank dans ZoneDepot et Recapitulatif)
export const urlFichier = (jeton: string, nom: string): string =>
  `${urlInstance(jeton)}/fichiers/${encodeURIComponent(nom)}`;

// ---------------------------------------------------------------------------
// Normalisation des erreurs
// ---------------------------------------------------------------------------

const estObjet = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v);

// Déjà normalisée (rejetée par une de nos fonctions, ou construite par `donnees`) : rendue telle quelle
const estErreurPublique = (e: unknown): e is ErreurPublique =>
  estObjet(e) && (e.status === null || typeof e.status === 'number') && typeof e.message === 'string'
  && !('response' in e) && !('isAxiosError' in e);

const tableauChaines = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);

// { version_reponses, visibles, sections_visibles, manquantes } d'un corps de réponse, ou null si absent
const lireEtatEcriture = (corps: Record<string, unknown>): EtatEcriture | null => {
  if (typeof corps.version_reponses !== 'number') return null;
  return {
    version_reponses: corps.version_reponses,
    visibles: tableauChaines(corps.visibles),
    sections_visibles: tableauChaines(corps.sections_visibles),
    manquantes: tableauChaines(corps.manquantes),
  };
};

// Erreur quelconque (axios, ErreurPublique, Error, autre) -> ErreurPublique.
//  - status : HTTP, ou null sans réponse du serveur (réseau, délai, backend arrêté) ;
//  - code / message / etat / erreurs pris du corps JSON quand il existe ; `ecriture` sur un 422 (brouillon enregistré) ;
//  - message par défaut (français) selon le status sinon.
export const normaliserErreur = (e: unknown): ErreurPublique => {
  if (estErreurPublique(e)) return e;
  const err: Record<string, unknown> = estObjet(e) ? e : {};
  const response = estObjet(err.response) ? err.response : null;
  const status = response && typeof response.status === 'number' ? response.status : null;
  const corps = response && estObjet(response.data) ? response.data : null;
  const r: ErreurPublique = { status, message: '' };
  if (corps) {
    if (typeof corps.code === 'string' && corps.code) r.code = corps.code;
    if (typeof corps.message === 'string' && corps.message.trim()) r.message = corps.message.trim();
    // `etat` n'est retenu que s'il est exploitable (version numérique et statut présents) : une version inventée
    // ferait boucler l'écran sur des 409 ; sans `etat`, l'écran affiche le message et propose de recharger
    if (estObjet(corps.etat)) {
      const etat = corps.etat;
      const version = etat.version_reponses;
      const statut = etat.statut;
      if (typeof version === 'number' && typeof statut === 'string') {
        r.etat = {
          reponses: estObjet(etat.reponses) ? (etat.reponses as unknown as Reponses) : null,
          version_reponses: version,
          statut: statut as StatutInstance,
        };
      }
    }
    if (Array.isArray(corps.erreurs)) {
      r.erreurs = corps.erreurs
        .filter((x): x is Record<string, unknown> => estObjet(x) && typeof x.question === 'string' && typeof x.message === 'string')
        .map((x): ErreurSoumission => ({ question: x.question as string, message: x.message as string }));
    }
    if (status === 422) {
      const ecriture = lireEtatEcriture(corps);
      if (ecriture) r.ecriture = ecriture;
    }
  }
  if (!r.message) {
    if (status !== null) r.message = MESSAGES_PAR_STATUT[status] || `Erreur inattendue (code ${status})`;
    else if (err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT') r.message = MESSAGE_DELAI;
    else if (err.isAxiosError === true || 'request' in err) r.message = MESSAGE_RESEAU;
    else r.message = `Erreur inattendue${typeof err.message === 'string' && err.message ? ` : ${err.message}` : ''}`;
  }
  return r;
};

// Corps d'une réponse 2xx : un objet JSON attendu (sinon ErreurPublique « réponse inattendue », ex. HTML d'un proxy)
const donnees = <T>(data: unknown, status: number): T => {
  if (!estObjet(data)) {
    const erreur: ErreurPublique = { status, message: 'Réponse inattendue du serveur : réessayez dans quelques instants' };
    throw erreur;
  }
  return data as unknown as T;
};

const config = (): AxiosRequestConfig => ({ timeout: DELAI_MS });

// Pourcentage 0..100 d'un événement de progression (total absent -> taille du fichier)
const pourcentage = (ev: AxiosProgressEvent, tailleFichier: number): number => {
  const total = typeof ev.total === 'number' && ev.total > 0 ? ev.total : tailleFichier;
  if (!(total > 0)) return 0;
  return Math.max(0, Math.min(100, Math.round((ev.loaded * 100) / total)));
};

// ---------------------------------------------------------------------------
// Appels
// ---------------------------------------------------------------------------

// GET /:jeton -> définition + brouillon + dossier (404 lien inconnu, 410 lien inactif, 503 indisponible)
export const charger = async (jeton: string): Promise<FormulaireCharge> => {
  try {
    const r = await axios.get(urlInstance(jeton), config());
    return donnees<FormulaireCharge>(r.data, r.status);
  } catch (e) {
    throw normaliserErreur(e);
  }
};

// PUT /:jeton/reponses -> état recalculé (409 code 'version' avec etat ; 409 'deja_soumis' ; 400 corps invalide)
export const sauvegarder = async (
  jeton: string, valeurs: Record<string, ValeurReponse>, version_reponses: number, section_courante?: string | null
): Promise<EtatEcriture> => {
  try {
    const r = await axios.put(
      `${urlInstance(jeton)}/reponses`,
      { valeurs, version_reponses, section_courante: section_courante ?? null },
      config()
    );
    return donnees<EtatEcriture>(r.data, r.status);
  } catch (e) {
    throw normaliserErreur(e);
  }
};

// POST /:jeton/fichiers/:question (multipart, seul champ « fichier ») -> 201 { fichier, version_reponses, … }
// Part sans sauvegarde préalable des valeurs (règle B). onProgress reçoit un pourcentage 0..100.
export const uploaderFichier = async (
  jeton: string, question: string, file: File, onProgress?: (pct: number) => void
): Promise<ReponseUpload> => {
  const fd = new FormData();
  fd.append('fichier', file, file.name);
  const options: AxiosRequestConfig = { timeout: DELAI_UPLOAD_MS };
  if (onProgress) options.onUploadProgress = (ev: AxiosProgressEvent) => onProgress(pourcentage(ev, file.size));
  try {
    const r = await axios.post(`${urlInstance(jeton)}/fichiers/${encodeURIComponent(question)}`, fd, options);
    return donnees<ReponseUpload>(r.data, r.status);
  } catch (e) {
    throw normaliserErreur(e);
  }
};

// DELETE /:jeton/fichiers/:nom -> état recalculé (404 fichier inconnu, 409 deja_soumis)
export const supprimerFichier = async (jeton: string, nom: string): Promise<EtatEcriture> => {
  try {
    const r = await axios.delete(urlFichier(jeton, nom), config());
    return donnees<EtatEcriture>(r.data, r.status);
  } catch (e) {
    throw normaliserErreur(e);
  }
};

// POST /:jeton/soumettre : enregistre (comme PUT) puis valide -> 200 { statut:'soumis', date_soumission,
// version_reponses, reponses purgées } ; 422 { erreurs, version_reponses, … } (brouillon conservé : ErreurPublique
// .erreurs + .ecriture) ; 409 version / deja_soumis
export const soumettre = async (
  jeton: string, valeurs: Record<string, ValeurReponse>, version_reponses: number, section_courante?: string | null
): Promise<ReponseSoumission> => {
  try {
    const r = await axios.post(
      `${urlInstance(jeton)}/soumettre`,
      { valeurs, version_reponses, section_courante: section_courante ?? null },
      config()
    );
    return donnees<ReponseSoumission>(r.data, r.status);
  } catch (e) {
    throw normaliserErreur(e);
  }
};
